import { atomWithMutation, atomWithQuery } from 'jotai-tanstack-query';
import { isAxiosError } from 'axios';

import { instance } from '@/interfaces';
import { selectedDeviceAtom } from '@/components/common/MapView';
import { userInfoAtom } from '@/interfaces/member';

import type { Getter } from 'jotai';
import type { AtomWithQueryResult } from 'jotai-tanstack-query';
import type { APIResponse } from '@/@types/types';
import type { Device, SiteSubscriptionsData, SiteSubscriptionsRow, SubscriptionData } from '@/@types/device';
import type { UserInfo } from '@/@types/user';

export const subscriptionAtom = atomWithQuery((get: Getter) => ({
  queryKey: ['SUBSCRIPTION', get(selectedDeviceAtom)],
  queryFn: async ({ queryKey: [, selectedDeviceAtomValue] }) => {
    const device = selectedDeviceAtomValue as Device;
    const data = await getSubscription(device.id);

    return data;
  },
}));
async function getSubscription(deviceId: string) {
  try {
    const { data } = await instance.get<APIResponse<SubscriptionData>>(`/subscription/sensor/${deviceId}`);
    return data.data;
  } catch (err) {
    if (isAxiosError(err)) {
      if (err.response) {
        console.error('[getSubscription] API error code:', err.response.data.errorCode);
      } else {
        console.error('[getSubscription] Axios error message:', err.message);
      }
    } else {
      console.error('[getSubscription] Unknown error', err);
    }
    return null;
  }
}

export const postSubscriptionAtom = atomWithMutation((get: Getter) => ({
  mutationFn: async ({ sensorId, plan }: { sensorId: string; plan: string }) => {
    const { data: userInfo } = get(userInfoAtom);
    const isSuccess = await postSubscription(sensorId, userInfo.site[0].id, plan);
    return isSuccess;
  },
}));
async function postSubscription(sensorId: string, siteId: string, plan: string) {
  try {
    const { data } = await instance.patch<APIResponse>(`/subscription/sensor/${sensorId}/site/${siteId}`, {
      plan,
    });
    if (data.errorCode !== 'OK') {
      console.error('[postSubscription] API error code:', data.errorCode);
      return false;
    }
    return true;
  } catch (err) {
    if (isAxiosError(err)) {
      if (err.response) {
        console.error('[postSubscription] API error code:', err.response.data.errorCode);
      } else {
        console.error('[postSubscription] Axios error message:', err.message);
      }
    } else {
      console.error('[postSubscription] Unknown error', err);
    }
    return false;
  }
}

export const siteSubscriptionsAtom = atomWithQuery((get: Getter) => ({
  queryKey: ['SITE_SUBSCRIPTIONS', get(userInfoAtom)],
  queryFn: async ({ queryKey: [, userInfoAtomValue] }) => {
    const { data: userInfo } = userInfoAtomValue as AtomWithQueryResult<UserInfo>;
    const data = await getSiteSubscriptions(userInfo.site[0].id);
    const dataWithId: SiteSubscriptionsRow[] = data.map((row) => ({ ...row, id: row.sensorId }));

    return dataWithId;
  },
}));
async function getSiteSubscriptions(siteId: string) {
  try {
    const { data } = await instance.get<APIResponse<SiteSubscriptionsData[]>>(`/subscription/site/${siteId}`);
    return data.data;
  } catch (err) {
    if (isAxiosError(err)) {
      if (err.response) {
        console.error('[getSiteSubscriptions] API error code:', err.response.data.errorCode);
      } else {
        console.error('[getSiteSubscriptions] Axios error message:', err.message);
      }
    } else {
      console.error('[getSiteSubscriptions] Unknown error', err);
    }
    return null;
  }
}
